// src/components/Header.jsx
import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Refresh, Download } from "@mui/icons-material";
import { ProgressDialog } from "../plungerPage/progressPopup";
import axios from "../../utils/axiosInstance";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function Header({
  onRefresh,
  onPoll,
  loading = false,
  deviceName,
  deviceId,
}) {
  const [isLoading, setIsLoading] = useState(loading);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");
  const pollRef = useRef(null);
  const handleRefresh = async () => {
    if (typeof onRefresh === "function" && !isLoading) {
      setIsLoading(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error("Refresh failed:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePoll = async () => {
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 1: get initial time
    const initialResp = await axios.get(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${deviceId}`
    );
    let lastTime = Number(initialResp.data.data?.time || 0);
    // Step 2: send G command
    console.log("🟢 Sending G command...");
    await axios.post(
      `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
      { unitId: deviceId, cmd: "G",UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: G`,},
      {
        headers: {
          id: "anything",
          "Content-Type": "application/json",
        },
      }
    );
    await new Promise((resolve) => setTimeout(resolve, 10000));
    // Step 3: start polling every second
    let counter = 0;
    pollRef.current = setInterval(async () => {
      try {
        counter++;
        console.log(`⏳ Checking device (${counter}s)...`);
        const secondResp = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${deviceId}`
        );
        const currentTime = Number(secondResp.data.data?.time || 0);
        console.log("currentTime", currentTime);
        if (currentTime && currentTime !== lastTime) {
          console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setTimeout(() => {
            setProgressSuccess(true);
          }, 5000);
        }

        if (counter > 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          console.warn("⏰ Timeout waiting for device update.");
          setProgressError(
            "⏰ Timeout waiting for device response. Please try again."
          );
        }
      } catch (err) {
        console.warn("Polling error:", err.message);
      }
    }, 4000); // poll every second
  };

  return (
    <Box
      className="header-row"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "rgba(255, 255, 255, 0.05)",
        borderRadius: "12px",
        padding: "2px 20px",
        border: "1px solid rgba(255, 255, 255, 0.05)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        color: "#f9fafb",
        backdropFilter: "blur(20px)",
        mb: 1,
      }}
    >
      {/* Device Info Section */}
      <Box
        className="device-info"
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Typography
          className="device-name"
          sx={{
            fontSize: {
              xs: "0.6rem",
              sm: "0.8rem",
              md: "1.2rem",
            },
            fontWeight: 700,
            color: "#f9fafb",
          }}
        >
          {deviceName}
        </Typography>
        <Typography
          className="device-id"
          sx={{
            fontSize: {
              xs: "0.5rem",
              sm: "0.7rem",
              md: "0.9rem",
            },
            color: "#6b7280",
          }}
        >
          {deviceId}
        </Typography>
      </Box>

      <Box className="header-buttons" sx={{ display: "flex", gap: 1 }}>
        {/* Refresh Button – always shown */}
        <Button
          onClick={handleRefresh}
          disabled={isLoading}
          sx={{
            backgroundColor: "#00BFFF",
            color: "white",
            borderRadius: "8px",
            fontWeight: 500,
            padding: "6px 12px",
            fontSize: {
              xs: "0.4rem",
              sm: "0.6rem",
              md: "0.875rem",
            },
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            transition: "all 0.2s",
            "&:hover:not(:disabled)": {
              backgroundColor: "#18a5d4ff",
            },
            "&:disabled": {
              opacity: 0.7,
              cursor: "not-allowed",
            },
          }}
        >
          <Refresh
            sx={{
              width: "14px",
              height: "14px",
              animation: isLoading ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          Refresh
        </Button>

        {/* Poll Button – only when onPoll is true */}
        {onPoll && (
          <Button
            onClick={handlePoll}
            disabled={progressOpen}
            sx={{
              backgroundColor: "#00BFFF",
              color: "white",
              borderRadius: "8px",
              fontWeight: 500,
              padding: "6px 12px",
              fontSize: {
                xs: "0.4rem",
                sm: "0.6rem",
                md: "0.875rem",
              },
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              transition: "all 0.2s",
              "&:hover:not(:disabled)": {
                backgroundColor: "#18a5d4ff",
              },
              "&:disabled": {
                opacity: 0.7,
                cursor: "not-allowed",
              },
            }}
          >
            <Download sx={{ width: "14px", height: "14px" }} />
            Poll
          </Button>
        )}
      </Box>

      {/* Progress Dialog */}
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressSuccess(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressSuccess(false);
          setProgressError("");
          if (typeof onPoll === "function") {
            await onPoll();
          }
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Box>
  );
}
